import { useAuth, authorizedFetch, parseApiErrorMessage } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface ConsultReportPayload {
  headline: string
  narrative: string
  talking_points: string[]
  questions_for_practitioner: string[]
  disclaimer: string
}

export interface ConsultReportItem {
  id: string
  intent_text: string
  visit_label: string
  visit_at: string | null
  status: 'pending' | 'ready' | 'failed' | string
  context_snapshot: Record<string, unknown>
  payload: ConsultReportPayload
  share_active: boolean
  share_expires_at: string | null
  thread_id: string
  created_at: string
  updated_at: string
}

export interface ConsultReportShareResult {
  share_url: string
  expires_at: string
}

export function useConsultReports() {
  const { token } = useAuth()
  const q = useQuery({
    queryKey: ['consult-reports'],
    queryFn: async (): Promise<ConsultReportItem[]> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch('/api/consultations/reports/')
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Failed to fetch consult reports'))
      }
      return response.json()
    },
    enabled: !!token,
    staleTime: 30 * 1000,
  })

  return {
    reports: q.data ?? [],
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  }
}

export function useConsultReport(reportId: string | undefined) {
  const { token } = useAuth()
  const q = useQuery({
    queryKey: ['consult-report', reportId],
    queryFn: async (): Promise<ConsultReportItem> => {
      if (!token) throw new Error('Not authenticated')
      if (!reportId) throw new Error('Missing report id')
      const response = await authorizedFetch(`/api/consultations/reports/${reportId}/`)
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Failed to fetch consult report'))
      }
      return response.json()
    },
    enabled: !!token && !!reportId,
    staleTime: 15 * 1000,
  })

  return {
    report: q.data,
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  }
}

export function useRegenerateConsultReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (reportId: string): Promise<ConsultReportItem> => {
      const response = await authorizedFetch(`/api/consultations/reports/${reportId}/regenerate/`, {
        method: 'POST',
        body: JSON.stringify({}),
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Failed to regenerate report'))
      }
      return response.json()
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['consult-reports'] })
      queryClient.invalidateQueries({ queryKey: ['consult-report', data.id] })
    },
  })
}

export function useCreateConsultShareLink() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (reportId: string): Promise<ConsultReportShareResult> => {
      const response = await authorizedFetch(`/api/consultations/reports/${reportId}/share/`, {
        method: 'POST',
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Failed to create share link'))
      }
      return response.json()
    },
    onSuccess: (_data, reportId) => {
      queryClient.invalidateQueries({ queryKey: ['consult-reports'] })
      queryClient.invalidateQueries({ queryKey: ['consult-report', reportId] })
    },
  })
}

export function useRevokeConsultShareLink() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (reportId: string): Promise<ConsultReportItem> => {
      const response = await authorizedFetch(
        `/api/consultations/reports/${reportId}/share/revoke/`,
        { method: 'POST' },
      )
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Failed to revoke share link'))
      }
      return response.json()
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['consult-reports'] })
      queryClient.invalidateQueries({ queryKey: ['consult-report', data.id] })
    },
  })
}

export function useDeleteConsultReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (reportId: string): Promise<void> => {
      const response = await authorizedFetch(`/api/consultations/reports/${reportId}/`, {
        method: 'DELETE',
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Failed to delete report'))
      }
    },
    onSuccess: (_data, reportId) => {
      queryClient.invalidateQueries({ queryKey: ['consult-reports'] })
      queryClient.removeQueries({ queryKey: ['consult-report', reportId] })
    },
  })
}

export async function downloadConsultReportPdf(reportId: string): Promise<void> {
  const response = await authorizedFetch(`/api/consultations/reports/${reportId}/pdf/`)
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Failed to download PDF'))
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `lumie-consult-prep-${reportId}.pdf`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
